import { useCallback } from "react";
import { UseFormReset, UseFormSetValue } from "react-hook-form";
import { SupplierType } from "../../../../config/enums/SupplierType.enum";
import { SupplierCreateOptions } from "../../../../config/enums/SupplierCreateOptions.enum";
import { IngredientDataType } from "../../../../config/enums/IngredientType.enum";
import {
  SupplierDataShape,
  SupplierIngredientDetails
} from "../supplierRegister.types";
import { shouldEnableEnvironmentalData } from "../supplierRegister.utils";

interface UseSupplierSelectionParams {
  ingredientId?: string;
  resetFields: () => void;
  applyIngredientDetails: (details?: SupplierIngredientDetails) => void;
  setValue: UseFormSetValue<Record<string, unknown>>;
  setViewEnvironmentalData?: (value: boolean) => void;
  setNewSupplier: (value: boolean) => void;
  setIsSupplierFormOpen: (value: boolean) => void;
  setIsSupplierAsUser: (value: boolean) => void;
  hideIngredientView: (value: boolean) => void;
  resetNewSupplier: UseFormReset<Record<string, unknown>>;
  supplierCurrentData: (data?: SupplierDataShape) => void;
  setSupplierData: React.Dispatch<React.SetStateAction<SupplierDataShape>>;
  setSupplier?: (data: SupplierDataShape) => void;
  loadPackageData?: (value: boolean) => void;
  checkCurrentSupplier?: (supplierId: unknown) => void;
}

export const useSupplierSelection = ({
  ingredientId,
  resetFields,
  applyIngredientDetails,
  setValue,
  setViewEnvironmentalData,
  setNewSupplier,
  setIsSupplierFormOpen,
  setIsSupplierAsUser,
  hideIngredientView,
  resetNewSupplier,
  supplierCurrentData,
  setSupplierData,
  setSupplier,
  loadPackageData,
  checkCurrentSupplier
}: UseSupplierSelectionParams) => {
  const closeSupplierData = useCallback(() => {
    setNewSupplier(false);
    setIsSupplierFormOpen(false);
    setIsSupplierAsUser(false);
    hideIngredientView(false);
    resetNewSupplier();
  }, [
    hideIngredientView,
    resetNewSupplier,
    setIsSupplierAsUser,
    setIsSupplierFormOpen,
    setNewSupplier
  ]);

  const saveValues = useCallback(
    (data: Record<string, unknown>, getSupplierIngredientDetails?: SupplierIngredientDetails) => {
      const supplierTitle =
        (data?.result as Record<string, unknown>)?.title || data?.supplier_name;
      const result = data?.result as Record<string, unknown> | undefined;
      const other = result?.other as Record<string, unknown> | undefined;
      const supplierDetails =
        other?.supplierDetails || result?.supplierDetails || data;
      const supplierDataType =
        getSupplierIngredientDetails?.data_type || IngredientDataType.USER;

      const nextSupplierData: SupplierDataShape = {
        supplier_name: supplierTitle as string,
        supplier_data: {
          ...(supplierDetails as Record<string, unknown>),
          dataType: supplierDataType
        },
        supplier_type:
          ((supplierDetails as Record<string, unknown>)?.supplier_type as SupplierType) ||
          SupplierType.External
      };

      applyIngredientDetails(getSupplierIngredientDetails);
      supplierCurrentData(nextSupplierData);
      setSupplierData(nextSupplierData);
      setViewEnvironmentalData?.(shouldEnableEnvironmentalData(getSupplierIngredientDetails));
      setValue("unit_size", getSupplierIngredientDetails?.unit_size || 0);
      setValue("ean_bar_code", getSupplierIngredientDetails?.ean_bar_code || 0);
      setValue("produceCultivar", getSupplierIngredientDetails?.produceCultivar || 0);
      setValue("product_id", getSupplierIngredientDetails?.product_id || 0);
      setSupplier?.(nextSupplierData);
      loadPackageData?.(true);

      const supplierId =
        (supplierDetails as Record<string, unknown>)?._id ||
        (supplierDetails as Record<string, unknown>)?.supplier_id;
      if (supplierId) {
        checkCurrentSupplier?.(supplierId);
      }
    },
    [
      applyIngredientDetails,
      checkCurrentSupplier,
      loadPackageData,
      setSupplier,
      setSupplierData,
      setValue,
      setViewEnvironmentalData,
      supplierCurrentData
    ]
  );

  const selectSupplier = useCallback(
    (supplierDetails: unknown, supplierTitle: string, supplier_type: SupplierType) => {
      const supplierData: SupplierDataShape = {
        supplier_name: supplierTitle,
        supplier_data: supplierDetails as SupplierDataShape["supplier_data"],
        supplier_type
      };
      resetFields();
      supplierCurrentData(supplierData);
      setSupplierData(supplierData);
      const supplierId =
        (supplierDetails as Record<string, unknown>)?._id ||
        (supplierDetails as Record<string, unknown>)?.supplier_id ||
        supplierDetails;
      checkCurrentSupplier?.(supplierId);
    },
    [checkCurrentSupplier, resetFields, setSupplierData, supplierCurrentData]
  );

  const createNewSupplier = useCallback(() => {
    setNewSupplier(true);
    hideIngredientView(true);
    supplierCurrentData(undefined);
    setSupplierData({});
  }, [hideIngredientView, setNewSupplier, setSupplierData, supplierCurrentData]);

  const saveSupplierData = useCallback(
    (data: Record<string, unknown>, supplierDetails: Record<string, unknown>, supplierTitle: string) => {
      const ingredientIds = (supplierDetails?.ingredientIds as string[]) || [];
      const checkNowItsAvailable = ingredientIds.some(e => e === ingredientId);

      if (checkNowItsAvailable) {
        const supplierIngredientDetails = (
          supplierDetails.supplierIngredientDetails as SupplierIngredientDetails[]
        )?.find(e => e.ingredient_id === ingredientId);
        setNewSupplier(false);
        setIsSupplierFormOpen(false);
        saveValues(data, supplierIngredientDetails);
      } else {
        selectSupplier(supplierDetails, supplierTitle, SupplierType.External);
      }
    },
    [ingredientId, saveValues, selectSupplier, setIsSupplierFormOpen, setNewSupplier]
  );

  const getSearchValue = useCallback(
    async (data: Record<string, unknown>) => {
      const result = data?.result as Record<string, unknown> | undefined;
      const other = result?.other as Record<string, unknown> | undefined;
      const supplierDetails = other?.supplierDetails || null;
      const supplierTitle = (result?.title as string) || "";
      const supplierId = (result?.id as number) || 0;
      checkCurrentSupplier?.(supplierId);

      switch (supplierId) {
        case SupplierCreateOptions.CREATE_NEW:
          createNewSupplier();
          break;
        case SupplierCreateOptions.SELECT_IN_HOUSE:
          selectSupplier(supplierId, supplierTitle, SupplierType.Internal);
          break;
        default:
          saveSupplierData(
            data,
            (supplierDetails as Record<string, unknown>) || {},
            supplierTitle
          );
          break;
      }
    },
    [checkCurrentSupplier, createNewSupplier, saveSupplierData, selectSupplier]
  );

  const handleSearchValidation = useCallback(
    (value: string) => {
      if (value) {
        resetFields();
      }
    },
    [resetFields]
  );

  return {
    closeSupplierData,
    saveValues,
    selectSupplier,
    createNewSupplier,
    saveSupplierData,
    getSearchValue,
    handleSearchValidation
  };
};
